// Main Three.js scene setup for garden visualization

import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { PointerLockControls } from 'three/addons/controls/PointerLockControls.js'
import { debug } from '@/utils/debug'

// Constants matching the existing implementation
// Editor uses 50 dots per meter
const DPM = 50
const DPM_SCALE = 1.0 / DPM

// Sky gradient shader
const skyVertexShader = `
  varying vec3 vWorldPosition;
  void main() {
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

const skyFragmentShader = `
  uniform vec3 topColor;
  uniform vec3 bottomColor;
  uniform float offset;
  uniform float exponent;
  varying vec3 vWorldPosition;

  void main() {
    float h = normalize(vWorldPosition + offset).y;
    gl_FragColor = vec4(
      mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)),
      1.0
    );
  }
`

export interface SceneOptions {
  container: HTMLElement
  onReady?: () => void
}

export type ViewMode = '2d' | '3d'

export type LayerName = 'plants' | 'buildings' | 'ground' | 'metadata'

export interface LayerVisibility {
  plants: boolean
  buildings: boolean
  ground: boolean
  metadata: boolean
}

export class GardenScene {
  private container: HTMLElement
  private scene: THREE.Scene
  private perspectiveCamera: THREE.PerspectiveCamera
  private orthographicCamera: THREE.OrthographicCamera
  private renderer: THREE.WebGLRenderer
  private controls: OrbitControls

  private animationId: number | null = null
  private isDisposed = false
  private dirLight: THREE.DirectionalLight | null = null
  private compassArrow: THREE.ArrowHelper | null = null
  private compassLabel: THREE.Sprite | null = null
  private gridHelper: THREE.GridHelper | null = null

  // View mode state
  private viewMode: ViewMode = '2d'
  private orthoViewSize: number = 50 // Meters visible vertically in 2D mode

  // Walk mode state
  private walkMode = false
  private walkControls: PointerLockControls | null = null
  private clock = new THREE.Clock()
  private walkSpeed = 5 // meters per second
  private moveForward = false
  private moveBackward = false
  private moveLeft = false
  private moveRight = false
  private onWalkModeChange: ((enabled: boolean) => void) | null = null
  private onLayerVisibilityChange: ((layer: LayerName, visible: boolean) => void) | null = null

  // Layer visibility state
  private layerVisibility: LayerVisibility = {
    plants: true,
    buildings: true,
    ground: true,
    metadata: true
  }

  // Groups for organizing objects
  public plantsGroup: THREE.Group
  public buildingsGroup: THREE.Group
  public groundGroup: THREE.Group

  constructor(options: SceneOptions) {
    this.container = options.container

    // Initialize Three.js components
    this.scene = new THREE.Scene()
    this.perspectiveCamera = this.createPerspectiveCamera()
    this.orthographicCamera = this.createOrthographicCamera()
    this.renderer = this.createRenderer()
    this.controls = this.createControls()

    // Apply initial view mode (2D by default)
    this.applyViewMode()

    // Create object groups
    this.plantsGroup = new THREE.Group()
    this.plantsGroup.name = 'plants'
    this.scene.add(this.plantsGroup)

    this.buildingsGroup = new THREE.Group()
    this.buildingsGroup.name = 'buildings'
    this.scene.add(this.buildingsGroup)

    this.groundGroup = new THREE.Group()
    this.groundGroup.name = 'ground'
    this.scene.add(this.groundGroup)

    // Setup scene elements
    this.setupLighting()
    this.setupSky()
    this.setupGround()
    this.setupGrid()

    // Setup debug helpers if debug mode is enabled
    if (debug.isEnabled()) {
      this.setupDebugHelpers()
    }

    // Handle resize
    window.addEventListener('resize', this.handleResize)

    // Setup walk mode controls
    this.setupWalkControls()

    // Start render loop
    this.animate()

    // Call onReady if provided (optional)
    if (options.onReady) {
      options.onReady()
    }
  }

  private createPerspectiveCamera(): THREE.PerspectiveCamera {
    const aspect = this.container.clientWidth / this.container.clientHeight
    const camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 5000)

    // Default position - looking at center from above and slightly to the side
    camera.position.set(30, 40, 30)
    camera.lookAt(0, 0, 0)

    return camera
  }

  private createOrthographicCamera(): THREE.OrthographicCamera {
    const aspect = this.container.clientWidth / this.container.clientHeight
    const viewSize = this.orthoViewSize

    const camera = new THREE.OrthographicCamera(
      -viewSize * aspect / 2,  // left
      viewSize * aspect / 2,   // right
      viewSize / 2,            // top
      -viewSize / 2,           // bottom
      0.1,                     // near
      1000                     // far
    )

    // Set up vector BEFORE lookAt (important for top-down view)
    // up = -Z means "north" (editor -Y) points to top of screen
    camera.up.set(0, 0, -1)

    // Position directly above, looking down
    camera.position.set(0, 100, 0)
    camera.lookAt(0, 0, 0)

    return camera
  }

  private createRenderer(): THREE.WebGLRenderer {
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false
    })

    renderer.setSize(this.container.clientWidth, this.container.clientHeight)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    // Shadow settings
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFSoftShadowMap

    // Color management
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.0

    this.container.appendChild(renderer.domElement)

    return renderer
  }

  private createControls(): OrbitControls {
    // Start with perspective camera, will be updated by applyViewMode()
    const controls = new OrbitControls(this.perspectiveCamera, this.renderer.domElement)

    // Configure controls for garden viewing
    controls.enableDamping = true
    controls.dampingFactor = 0.05

    // Limit vertical rotation (don't go below ground)
    controls.maxPolarAngle = Math.PI / 2 - 0.1

    // Zoom limits (for perspective camera)
    controls.minDistance = 5
    controls.maxDistance = 200

    // Pan settings
    controls.enablePan = true
    controls.panSpeed = 0.8
    controls.screenSpacePanning = true

    // Target at ground level
    controls.target.set(0, 0, 0)

    return controls
  }

  /**
   * Apply view mode settings to camera and controls
   */
  private applyViewMode(): void {
    if (this.viewMode === '2d') {
      // 2D mode: orthographic camera, pan+zoom only
      this.controls.object = this.orthographicCamera
      this.controls.enableRotate = false
      this.controls.minDistance = 0
      this.controls.maxDistance = Infinity
      // Position ortho camera above current target
      this.orthographicCamera.position.set(
        this.controls.target.x,
        100,
        this.controls.target.z
      )
      this.orthographicCamera.lookAt(
        this.controls.target.x,
        0,
        this.controls.target.z
      )
      this.updateOrthographicBounds()
    } else {
      // 3D mode: perspective camera, full orbit
      this.controls.object = this.perspectiveCamera
      this.controls.enableRotate = true
      this.controls.minDistance = 5
      this.controls.maxDistance = 200
    }

    debug.log('Scene', `View mode applied: ${this.viewMode}`)
  }

  /**
   * Update orthographic camera bounds based on container size and zoom level
   */
  private updateOrthographicBounds(): void {
    const aspect = this.container.clientWidth / this.container.clientHeight
    const viewSize = this.orthoViewSize

    this.orthographicCamera.left = -viewSize * aspect / 2
    this.orthographicCamera.right = viewSize * aspect / 2
    this.orthographicCamera.top = viewSize / 2
    this.orthographicCamera.bottom = -viewSize / 2
    this.orthographicCamera.updateProjectionMatrix()
  }

  /**
   * Get the currently active camera based on view mode
   */
  private getActiveCamera(): THREE.Camera {
    return this.viewMode === '2d' ? this.orthographicCamera : this.perspectiveCamera
  }

  private setupLighting(): void {
    // Hemisphere light for ambient illumination
    const hemiLight = new THREE.HemisphereLight(
      new THREE.Color().setHSL(0.6, 1, 0.6), // Sky color
      new THREE.Color().setHSL(0.095, 1, 0.75), // Ground color
      2.0
    )
    hemiLight.position.set(0, 50, 0)
    this.scene.add(hemiLight)

    // Directional light (sun) with shadows
    this.dirLight = new THREE.DirectionalLight(0xffffff, 1.5)
    this.dirLight.position.set(50, 100, 50)
    this.dirLight.castShadow = true

    // Shadow camera setup
    const shadowSize = 100
    this.dirLight.shadow.camera.left = -shadowSize
    this.dirLight.shadow.camera.right = shadowSize
    this.dirLight.shadow.camera.top = shadowSize
    this.dirLight.shadow.camera.bottom = -shadowSize
    this.dirLight.shadow.camera.near = 0.5
    this.dirLight.shadow.camera.far = 500

    // Shadow quality
    this.dirLight.shadow.mapSize.width = 2048
    this.dirLight.shadow.mapSize.height = 2048
    this.dirLight.shadow.bias = -0.0001

    this.scene.add(this.dirLight)

    // Subtle fill light from opposite direction
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3)
    fillLight.position.set(-50, 50, -50)
    this.scene.add(fillLight)
  }

  private setupSky(): void {
    // Create sky dome with gradient shader
    const skyGeo = new THREE.SphereGeometry(400, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2)
    const skyMat = new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: new THREE.Color(0x0077ff) },
        bottomColor: { value: new THREE.Color(0x89cff0) },
        offset: { value: 33 },
        exponent: { value: 0.6 }
      },
      vertexShader: skyVertexShader,
      fragmentShader: skyFragmentShader,
      side: THREE.BackSide
    })

    const sky = new THREE.Mesh(skyGeo, skyMat)
    this.scene.add(sky)

    // Add fog for depth
    this.scene.fog = new THREE.Fog(0x89cff0, 100, 400)
  }

  private setupGround(): void {
    // Large ground plane (below scheme layer)
    const groundGeo = new THREE.PlaneGeometry(500, 500)
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x8b7355, // Earth brown
      roughness: 1.0,
      metalness: 0.0
    })

    const ground = new THREE.Mesh(groundGeo, groundMat)
    ground.rotation.x = -Math.PI / 2
    ground.position.y = -0.05 // Below scheme layer (-0.02) so scheme is visible
    ground.receiveShadow = true

    this.scene.add(ground)
  }

  /**
   * Setup grid helper (always created, visibility controlled by view mode)
   */
  private setupGrid(): void {
    const gridSize = 100 // 100 meters total
    const gridDivisions = 100 // 1 meter per division

    this.gridHelper = new THREE.GridHelper(
      gridSize,
      gridDivisions,
      0x444444, // Center line color
      0x222222  // Grid line color
    )
    this.gridHelper.position.y = 0.005 // Just above ground
    // Set initial visibility based on view mode (default is 2D, so grid is visible)
    this.gridHelper.visible = this.viewMode === '2d'
    this.scene.add(this.gridHelper)
  }

  /**
   * Set grid visibility
   */
  public setGridVisible(visible: boolean): void {
    if (this.gridHelper) {
      this.gridHelper.visible = visible
    }
  }

  private setupDebugHelpers(): void {
    debug.log('Scene', 'Setting up debug helpers (axes + compass)')

    // Add axes helper at origin
    // Red = X, Green = Y, Blue = Z
    const axesHelper = new THREE.AxesHelper(5) // 5 meters long
    axesHelper.position.set(0, 0.01, 0) // Slightly above ground
    this.scene.add(axesHelper)

    // Make grid visible in debug mode (regardless of view mode)
    this.setGridVisible(true)

    // Add axis labels using sprites
    this.addAxisLabel('X', new THREE.Vector3(5.5, 0.5, 0), 0xff0000)
    this.addAxisLabel('Y', new THREE.Vector3(0, 5.5, 0), 0x00ff00)
    this.addAxisLabel('Z', new THREE.Vector3(0, 0.5, 5.5), 0x0000ff)

    // Add compass needle pointing North (initially -Z direction when rotation=0)
    this.createCompassNeedle()

    debug.success('Scene', 'Debug helpers added')
  }

  private createCompassNeedle(): void {
    // Remove existing compass if present
    if (this.compassArrow) {
      this.scene.remove(this.compassArrow)
      this.compassArrow.dispose()
    }
    if (this.compassLabel) {
      this.scene.remove(this.compassLabel)
      this.compassLabel.material.dispose()
    }

    // North direction: -Z when rotation=0
    const northDir = new THREE.Vector3(0, 0, -1)
    const origin = new THREE.Vector3(0, 0.5, 0) // Slightly above ground
    const length = 8
    const headLength = 1.5
    const headWidth = 0.8

    // Create arrow in magenta/pink color (distinct from RGB axes)
    this.compassArrow = new THREE.ArrowHelper(
      northDir,
      origin,
      length,
      0xff00ff, // Magenta
      headLength,
      headWidth
    )
    this.scene.add(this.compassArrow)

    // Add "N" label at the tip
    this.compassLabel = this.createCompassLabel()
    this.updateCompassLabelPosition(northDir, origin, length)
    this.scene.add(this.compassLabel)
  }

  private createCompassLabel(): THREE.Sprite {
    const canvas = document.createElement('canvas')
    const size = 64
    canvas.width = size
    canvas.height = size

    const ctx = canvas.getContext('2d')!
    // White background circle
    ctx.fillStyle = '#ff00ff'
    ctx.beginPath()
    ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2)
    ctx.fill()
    // Black "N" text
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 40px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('N', size / 2, size / 2)

    const texture = new THREE.CanvasTexture(canvas)
    const material = new THREE.SpriteMaterial({ map: texture })
    const sprite = new THREE.Sprite(material)
    sprite.scale.set(1.5, 1.5, 1)

    return sprite
  }

  private updateCompassLabelPosition(direction: THREE.Vector3, origin: THREE.Vector3, length: number): void {
    if (!this.compassLabel) return
    // Position label at the tip of the arrow, slightly above
    const tipPosition = origin.clone().add(direction.clone().multiplyScalar(length + 1))
    tipPosition.y += 0.5
    this.compassLabel.position.copy(tipPosition)
  }

  private addAxisLabel(text: string, position: THREE.Vector3, color: number): void {
    // Create canvas for text
    const canvas = document.createElement('canvas')
    const size = 64
    canvas.width = size
    canvas.height = size

    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`
    ctx.font = 'bold 48px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, size / 2, size / 2)

    // Create sprite
    const texture = new THREE.CanvasTexture(canvas)
    const material = new THREE.SpriteMaterial({ map: texture })
    const sprite = new THREE.Sprite(material)
    sprite.position.copy(position)
    sprite.scale.set(1, 1, 1)

    this.scene.add(sprite)
  }

  private setupWalkControls(): void {
    // Create PointerLockControls for first-person look
    this.walkControls = new PointerLockControls(this.perspectiveCamera, this.renderer.domElement)

    // When pointer lock is lost (e.g., pressing Escape), exit walk mode
    this.walkControls.addEventListener('unlock', () => {
      if (this.walkMode) {
        this.setWalkMode(false)
      }
    })

    // Add keyboard listeners for WASD movement
    document.addEventListener('keydown', this.handleKeyDown)
    document.addEventListener('keyup', this.handleKeyUp)
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (!this.walkMode) return

    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.moveForward = true
        break
      case 'KeyS':
      case 'ArrowDown':
        this.moveBackward = true
        break
      case 'KeyA':
      case 'ArrowLeft':
        this.moveLeft = true
        break
      case 'KeyD':
      case 'ArrowRight':
        this.moveRight = true
        break
    }
  }

  private handleKeyUp = (event: KeyboardEvent): void => {
    if (!this.walkMode) return

    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.moveForward = false
        break
      case 'KeyS':
      case 'ArrowDown':
        this.moveBackward = false
        break
      case 'KeyA':
      case 'ArrowLeft':
        this.moveLeft = false
        break
      case 'KeyD':
      case 'ArrowRight':
        this.moveRight = false
        break
    }
  }

  private handleResize = (): void => {
    if (this.isDisposed) return

    const width = this.container.clientWidth
    const height = this.container.clientHeight

    // Update perspective camera
    this.perspectiveCamera.aspect = width / height
    this.perspectiveCamera.updateProjectionMatrix()

    // Update orthographic camera
    this.updateOrthographicBounds()

    this.renderer.setSize(width, height)
  }

  private animate = (): void => {
    if (this.isDisposed) return

    this.animationId = requestAnimationFrame(this.animate)

    const delta = this.clock.getDelta()

    // Handle walk mode movement
    if (this.walkMode && this.walkControls) {
      // Calculate movement direction based on WASD keys
      const moveX = (this.moveRight ? 1 : 0) - (this.moveLeft ? 1 : 0)
      const moveZ = (this.moveBackward ? 1 : 0) - (this.moveForward ? 1 : 0)

      if (moveX !== 0 || moveZ !== 0) {
        // Apply movement in camera's local space
        this.walkControls.moveRight(moveX * this.walkSpeed * delta)
        this.walkControls.moveForward(-moveZ * this.walkSpeed * delta)
      }

      // Lock camera Y position to eye level (1.6m)
      this.perspectiveCamera.position.y = 1.6
    } else {
      // Update orbit controls when not in walk mode
      this.controls.update()

      // In 2D mode, sync ortho camera position with controls target (for panning)
      if (this.viewMode === '2d') {
        this.orthographicCamera.position.x = this.controls.target.x
        this.orthographicCamera.position.z = this.controls.target.z
      } else {
        // In 3D mode, prevent camera from going below ground
        if (this.controls.target.y < 0) {
          this.controls.target.y = 0
        }
      }
    }

    // Render with active camera
    this.renderer.render(this.scene, this.getActiveCamera())
  }

  /**
   * Convert editor coordinates to Three.js coordinates
   * Editor: x=right, y=down, z=elevation
   * Three.js: x=right, y=up, z=forward
   */
  public editorToWorld(x: number, y: number, elevation: number = 0): THREE.Vector3 {
    return new THREE.Vector3(
      x * DPM_SCALE,
      elevation * DPM_SCALE,
      y * DPM_SCALE
    )
  }

  /**
   * Convert editor size to Three.js scale
   */
  public editorSizeToWorld(width: number, height: number): { width: number; height: number } {
    return {
      width: width * DPM_SCALE,
      height: height * DPM_SCALE
    }
  }

  /**
   * Focus camera on a specific point, optionally fitting to bounds
   * @param x - Center X in editor coordinates
   * @param y - Center Y in editor coordinates
   * @param z - Center Z (elevation) in editor coordinates
   * @param boundsWidth - Optional width in editor coordinates to fit
   * @param boundsHeight - Optional height in editor coordinates to fit
   */
  public focusOn(x: number, y: number, z: number = 0, boundsWidth?: number, boundsHeight?: number): void {
    const target = this.editorToWorld(x, y, z)
    this.controls.target.copy(target)

    // Calculate view size to fit bounds (if provided)
    if (boundsWidth !== undefined && boundsHeight !== undefined) {
      const worldWidth = boundsWidth * DPM_SCALE
      const worldHeight = boundsHeight * DPM_SCALE
      const aspect = this.container.clientWidth / this.container.clientHeight

      // Calculate view size to fit the larger dimension with padding
      const padding = 1.2 // 20% padding
      if (worldWidth / aspect > worldHeight) {
        // Width is limiting factor
        this.orthoViewSize = (worldWidth / aspect) * padding
      } else {
        // Height is limiting factor
        this.orthoViewSize = worldHeight * padding
      }

      // Also set reasonable perspective distance
      const perspectiveDistance = Math.max(worldWidth, worldHeight) * 0.8

      if (this.viewMode === '2d') {
        this.updateOrthographicBounds()
      }

      // Update perspective camera position based on bounds
      this.perspectiveCamera.position.set(
        target.x + perspectiveDistance * 0.5,
        perspectiveDistance * 0.7,
        target.z + perspectiveDistance * 0.5
      )
    } else {
      // No bounds - use default positioning
      this.perspectiveCamera.position.set(
        target.x + 20,
        target.y + 30,
        target.z + 20
      )
    }

    if (this.viewMode === '2d') {
      // Position ortho camera directly above target
      this.orthographicCamera.position.set(target.x, 100, target.z)
      this.orthographicCamera.lookAt(target.x, 0, target.z)
    }
  }

  /**
   * Clear all objects from a specific group
   */
  public clearGroup(group: THREE.Group): void {
    while (group.children.length > 0) {
      const child = group.children[0]
      group.remove(child)

      // Dispose of geometries and materials
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose()
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose())
        } else {
          child.material?.dispose()
        }
      }
    }
  }

  /**
   * Clear all garden objects (keep scene elements)
   */
  public clearAllObjects(): void {
    this.clearGroup(this.plantsGroup)
    this.clearGroup(this.buildingsGroup)
    this.clearGroup(this.groundGroup)
  }

  /**
   * Get the Three.js scene (for adding custom objects)
   */
  public getScene(): THREE.Scene {
    return this.scene
  }

  /**
   * Get the active camera
   */
  public getCamera(): THREE.Camera {
    return this.getActiveCamera()
  }

  /**
   * Get the current view mode
   */
  public getViewMode(): ViewMode {
    return this.viewMode
  }

  /**
   * Set the view mode (2D or 3D)
   */
  public setViewMode(mode: ViewMode): void {
    if (mode === this.viewMode) return

    // Exit walk mode when switching to 2D
    if (mode === '2d' && this.walkMode) {
      this.setWalkMode(false)
    }

    debug.log('Scene', `Switching view mode: ${this.viewMode} → ${mode}`)

    // When switching from 3D to 2D, estimate ortho zoom from perspective distance
    if (mode === '2d' && this.viewMode === '3d') {
      const distance = this.perspectiveCamera.position.distanceTo(this.controls.target)
      // Rough mapping: perspective distance to ortho view size
      this.orthoViewSize = Math.max(10, Math.min(200, distance * 1.2))
    }

    // When switching from 2D to 3D, position perspective camera based on ortho view
    if (mode === '3d' && this.viewMode === '2d') {
      const target = this.controls.target.clone()
      const distance = this.orthoViewSize * 0.8
      this.perspectiveCamera.position.set(
        target.x + distance * 0.5,
        distance * 0.7,
        target.z + distance * 0.5
      )
    }

    this.viewMode = mode
    this.applyViewMode()

    // Show grid in 2D mode or when debug is enabled
    // Grid visibility respects layer visibility, view mode, and debug mode
    this.setGridVisible(this.layerVisibility.metadata && (mode === '2d' || debug.isEnabled()))
  }

  /**
   * Check if walk mode is currently active
   */
  public isWalkMode(): boolean {
    return this.walkMode
  }

  /**
   * Enable or disable walk mode (first-person view)
   * Walk mode is only available in 3D view
   */
  public setWalkMode(enabled: boolean): void {
    // Can't enter walk mode in 2D view
    if (enabled && this.viewMode === '2d') {
      debug.warn('Scene', 'Cannot enter walk mode in 2D view')
      return
    }

    if (enabled === this.walkMode) return

    this.walkMode = enabled

    if (enabled && this.walkControls) {
      // Save current orbit camera state before entering walk mode
      const target = this.controls.target.clone()

      // Disable orbit controls
      this.controls.enabled = false

      // Position camera at current target, at eye level
      this.perspectiveCamera.position.set(target.x, 1.6, target.z)
      this.perspectiveCamera.lookAt(target.x, 1.6, target.z - 1)

      // Lock the pointer for first-person controls
      this.walkControls.lock()

      debug.log('Scene', 'Walk mode enabled')
    } else {
      // Reset movement flags
      this.moveForward = false
      this.moveBackward = false
      this.moveLeft = false
      this.moveRight = false

      // Unlock pointer if locked
      if (this.walkControls?.isLocked) {
        this.walkControls.unlock()
      }

      // Re-enable orbit controls
      this.controls.enabled = true

      // Update orbit target to current camera position
      const camPos = this.perspectiveCamera.position.clone()
      this.controls.target.set(camPos.x, 0, camPos.z)

      // Move camera back to orbit position
      const distance = 20
      this.perspectiveCamera.position.set(
        camPos.x + distance * 0.5,
        distance * 0.7,
        camPos.z + distance * 0.5
      )

      debug.log('Scene', 'Walk mode disabled')
    }

    // Notify listeners
    if (this.onWalkModeChange) {
      this.onWalkModeChange(this.walkMode)
    }
  }

  /**
   * Set callback for walk mode changes
   */
  public setOnWalkModeChange(callback: (enabled: boolean) => void): void {
    this.onWalkModeChange = callback
  }

  /**
   * Set sun/directional light position based on garden rotation
   * @param gardenRotation - The garden's north direction rotation in degrees (clockwise from top)
   *
   * At noon, 45° latitude, northern hemisphere:
   * - Sun is in the south direction
   * - Sun elevation is approximately 45° above horizon
   *
   * With rotation=0: North = -Z (top of 2D view), South = +Z
   * With rotation=R: Rotate sun position around Y-axis by R degrees
   */
  public setSunDirection(gardenRotation: number): void {
    if (!this.dirLight) return

    // Normalize rotation to 0-360
    const rotation = ((gardenRotation % 360) + 360) % 360
    const rotationRad = rotation * Math.PI / 180

    // Sun from south at 45° elevation
    // Base direction: +Z (south when rotation=0)
    const sunDistance = 100
    const sunElevation = Math.PI / 4  // 45 degrees

    // Calculate sun position rotated around Y axis
    // Sun comes from the south (opposite of north)
    // North = (sin(θ), 0, -cos(θ)), so South = (-sin(θ), 0, cos(θ))
    const sunX = -Math.sin(rotationRad) * sunDistance * Math.cos(sunElevation)
    const sunY = sunDistance * Math.sin(sunElevation)
    const sunZ = Math.cos(rotationRad) * sunDistance * Math.cos(sunElevation)

    this.dirLight.position.set(sunX, sunY, sunZ)

    // Update compass needle direction (in debug mode)
    this.updateCompassDirection(rotationRad)

    debug.log('Scene', `Sun direction set for garden rotation: ${gardenRotation}° → position (${sunX.toFixed(1)}, ${sunY.toFixed(1)}, ${sunZ.toFixed(1)})`)
  }

  /**
   * Update compass needle to point North based on garden rotation
   * @param rotationRad - Garden rotation in radians (clockwise from top)
   */
  private updateCompassDirection(rotationRad: number): void {
    if (!this.compassArrow) return

    // Calculate new north direction
    // When rotation=0, north is -Z
    // Rotation is clockwise from above (positive = clockwise)
    const northX = Math.sin(rotationRad)
    const northZ = -Math.cos(rotationRad)
    const northDir = new THREE.Vector3(northX, 0, northZ).normalize()

    // Update arrow direction
    this.compassArrow.setDirection(northDir)

    // Update label position
    const origin = new THREE.Vector3(0, 0.5, 0)
    const length = 8
    this.updateCompassLabelPosition(northDir, origin, length)

    debug.log('Scene', `Compass updated: North direction (${northX.toFixed(2)}, 0, ${northZ.toFixed(2)})`)
  }

  /**
   * Set visibility of a specific layer
   */
  public setLayerVisible(layer: LayerName, visible: boolean): void {
    this.layerVisibility[layer] = visible

    switch (layer) {
      case 'plants':
        this.plantsGroup.visible = visible
        break
      case 'buildings':
        this.buildingsGroup.visible = visible
        break
      case 'ground':
        this.groundGroup.visible = visible
        break
      case 'metadata':
        // Grid visibility respects both layer toggle AND view mode/debug mode
        if (this.gridHelper) {
          this.gridHelper.visible = visible && (this.viewMode === '2d' || debug.isEnabled())
        }
        // Note: schemeImage and schemeOutline are managed by SchemeRenderer
        break
    }

    debug.log('Scene', `Layer '${layer}' visibility: ${visible}`)

    // Notify listeners
    if (this.onLayerVisibilityChange) {
      this.onLayerVisibilityChange(layer, visible)
    }
  }

  /**
   * Toggle visibility of a specific layer
   * @returns The new visibility state
   */
  public toggleLayer(layer: LayerName): boolean {
    const newState = !this.layerVisibility[layer]
    this.setLayerVisible(layer, newState)
    return newState
  }

  /**
   * Get current layer visibility state
   */
  public getLayerVisibility(): LayerVisibility {
    return { ...this.layerVisibility }
  }

  /**
   * Check if a specific layer is visible
   */
  public isLayerVisible(layer: LayerName): boolean {
    return this.layerVisibility[layer]
  }

  /**
   * Set callback for layer visibility changes
   */
  public setOnLayerVisibilityChange(callback: (layer: LayerName, visible: boolean) => void): void {
    this.onLayerVisibilityChange = callback
  }

  /**
   * Dispose of all resources
   */
  public dispose(): void {
    this.isDisposed = true

    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
    }

    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('keydown', this.handleKeyDown)
    document.removeEventListener('keyup', this.handleKeyUp)

    this.controls.dispose()
    this.walkControls?.dispose()
    this.renderer.dispose()

    // Remove canvas from DOM
    this.container.removeChild(this.renderer.domElement)
  }
}
